// Quiet hours for "Approve & send" (SECOND-TOUCH-2026-10-02 §4) -- the recruiter agent's
// window, ported verbatim from recruiter-agent src/brain.js (QUIET_START_HOUR,
// QUIET_END_HOUR, DEFAULT_TZ, UNKNOWN_STATE_START_HOUR, STATE_TZ, withinSendWindow).
//
// Why the CRM needs its own copy: the staff send path (/staff-reply) does NOT apply quiet
// hours -- a human reply is sent at once. A second-touch draft is outreach, so the CRM
// refuses to send it outside the lead's window and holds it instead, showing when the
// window opens. quietHours.test.js pins these constants against the worker's file when a
// sibling checkout is on disk.

export const QUIET_START_HOUR = 8; // 8:00 local, inclusive -- window opens
export const QUIET_END_HOUR = 20; // 20:00 local, exclusive -- window closes (21:00 until 2026-10-28: NIGHT-AGENTS-2026-10-28 §3, the hub's rule)
export const DEFAULT_TZ = 'America/New_York';
// Unknown state: 12:00-20:00 Eastern (= 09:00 Pacific - 20:00 Eastern every day of the year), tightened from
// 11:00-21:00 on 2026-10-02 in the agent and here together (Matt's ruling, DINNER-AGENTS GATE).
export const UNKNOWN_STATE_START_HOUR = 12; // 12:00 Eastern (= 09:00 Pacific), inclusive
export const UNKNOWN_STATE_END_HOUR = 20; // 20:00 Eastern, exclusive

export const STATE_TZ = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver', CT: 'America/New_York', DE: 'America/New_York',
  FL: 'America/New_York', GA: 'America/New_York', HI: 'Pacific/Honolulu', ID: 'America/Boise',
  IL: 'America/Chicago', IN: 'America/Indiana/Indianapolis', IA: 'America/Chicago', KS: 'America/Chicago',
  KY: 'America/New_York', LA: 'America/Chicago', ME: 'America/New_York', MD: 'America/New_York',
  MA: 'America/New_York', MI: 'America/New_York', MN: 'America/Chicago', MS: 'America/Chicago',
  MO: 'America/Chicago', MT: 'America/Denver', NE: 'America/Chicago', NV: 'America/Los_Angeles',
  NH: 'America/New_York', NJ: 'America/New_York', NM: 'America/Denver', NY: 'America/New_York',
  NC: 'America/New_York', ND: 'America/Chicago', OH: 'America/New_York', OK: 'America/Chicago',
  OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York',
  SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago', UT: 'America/Denver',
  VT: 'America/New_York', VA: 'America/New_York', WA: 'America/Los_Angeles', WV: 'America/New_York',
  WI: 'America/Chicago', WY: 'America/Denver', DC: 'America/New_York',
};

export function resolveTimezone(state) {
  if (!state) return null;
  return STATE_TZ[String(state).toUpperCase().trim()] || null;
}

function localHour(nowMs, tz) {
  const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false }).format(new Date(nowMs));
  return parseInt(s, 10) % 24;
}

/** True when a candidate-facing text may be sent now (NOT quiet hours). */
export function withinSendWindow(nowMs, timezone) {
  if (timezone) {
    const hr = localHour(nowMs, timezone);
    return hr >= QUIET_START_HOUR && hr < QUIET_END_HOUR;
  }
  const et = localHour(nowMs, DEFAULT_TZ);
  return et >= UNKNOWN_STATE_START_HOUR && et < UNKNOWN_STATE_END_HOUR;
}

/** The first moment (on a 5-minute grid, within 24 h) the window is open for `state`. */
export function nextSendTime(nowMs, state) {
  const tz = resolveTimezone(state);
  if (withinSendWindow(nowMs, tz)) return new Date(nowMs);
  const step = 5 * 60 * 1000;
  const start = Math.ceil(nowMs / step) * step;
  for (let t = start; t <= nowMs + 24 * 3600 * 1000; t += step) {
    if (withinSendWindow(t, tz)) return new Date(t);
  }
  return null;
}

// ─── second-touch drafts ─────────────────────────────────────────────────────────────────
// DINNER-AGENTS-2026-10-02 §3: the census found 88 of 93 consented leads, and every lead with a
// draft, with no leads.state. Such a lead is held to the strictest window -- 09:00 Pacific to 20:00
// Eastern, now the agent's own unknown-state window too (withinSendWindow above) -- and the CRM says
// so on the draft and asks Matt to confirm before the text goes.
export const STATE_UNKNOWN_NOTE = 'State unknown — confirm before sending';

/** True when a second-touch text may be sent now to a lead in `state` (null/unmapped = the strictest window). */
export function withinDraftSendWindow(nowMs, state) {
  return withinSendWindow(nowMs, resolveTimezone(state));
}

/** The first moment (5-minute grid, within 24 h) a second-touch text may go to a lead in `state`. */
export function nextDraftSendTime(nowMs, state) {
  return nextSendTime(nowMs, state);
}

/** "8:00 AM CDT" style label in the lead's zone (Eastern when the state is unknown). */
export function sendTimeLabel(date, state) {
  const tz = resolveTimezone(state) || DEFAULT_TZ;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(date);
}
